import type { jsPDF } from "jspdf";
import type { CellHookData } from "jspdf-autotable";
import type { DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import type { ExportRow, RowKind } from "./exportStyledExcel";

// jspdf + jspdf-autotable are lazy-loaded (dynamic import) so they are
// code-split out of the main bundle — only fetched when a user exports a PDF.
type AutoTableFn = typeof import("jspdf-autotable")["autoTable"];

async function loadPdfLibs() {
  const [{ jsPDF: JsPDF }, { autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  return { JsPDF, autoTable };
}

/**
 * Shared PDF renderer for the finance reports. Consumes the SAME ExportRow
 * model as exportStyledExcel, so the on-page look matches the Excel export and
 * the UI: dark title band, slate header, section headers, emerald highlights,
 * percentage rows, amber reconciliation lines, dark grand totals, and
 * colour-by-sign values. Vector text (crisp, selectable) with page breaks.
 *
 * `exportStyledPdf` renders one report; `exportStyledPdfMulti` stitches several
 * reports into one document (each starting on a new page) — used for the
 * combined investor PDF.
 */

type RGB = [number, number, number];

const C: Record<string, RGB> = {
  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate600: [71, 85, 105],
  slate500: [100, 116, 139],
  slate300: [203, 213, 225],
  slate200: [226, 232, 240],
  slate100: [241, 245, 249],
  slate50: [248, 250, 252],
  white: [255, 255, 255],
  emerald800: [6, 95, 70],
  emerald700: [4, 120, 87],
  emerald600: [5, 150, 105],
  emerald50: [236, 253, 245],
  amber900: [120, 53, 15],
  amber50: [255, 251, 235],
  rose500: [244, 63, 94],
  red600: [220, 38, 38],
};

const FONT = "helvetica";
const SIDE = 28;
const BAND_TOP = 24;
const BAND_H = 34;
const CONTENT_TOP = BAND_TOP + BAND_H + 8;

export type PdfSection = {
  title: string;
  periodLabels: { key: string; label: string }[];
  rows: ExportRow[];
  displayCurrency: DisplayCurrency;
  currencyDecimals?: number;
};

function fmtCurrency(v: number, currency: DisplayCurrency, decimals: number): string {
  if (v === 0) return "–";
  const sym = currency === "USD" ? "$" : "ETB ";
  const abs = Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return v < 0 ? `(${sym}${abs})` : `${sym}${abs}`;
}

function fmtPercent(v: number): string {
  if (!Number.isFinite(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function formatCell(
  value: number,
  format: "currency" | "percent" | "plain" | undefined,
  kind: RowKind,
  currency: DisplayCurrency,
  decimals: number
): string {
  const f = format ?? (kind === "percent" ? "percent" : "currency");
  if (f === "percent") return fmtPercent(value);
  if (f === "plain")
    return value === 0 ? "–" : value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return fmtCurrency(value, currency, decimals);
}

function widePage(maxCols: number): "a3" | "a4" {
  return maxCols > 8 ? "a3" : "a4";
}

/** Render one report section into the current position of `doc`. */
function drawSection(doc: jsPDF, autoTable: AutoTableFn, section: PdfSection): void {
  const { title, periodLabels, rows, displayCurrency, currencyDecimals = 2 } = section;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const head = [["LINE ITEM", ...periodLabels.map((p) => p.label.toUpperCase())]];
  const body = rows.map((r) =>
    [r.label].concat(
      periodLabels.map((_p, i) => {
        if (r.kind === "header") return "";
        const spec = r.cells?.[i];
        if (!spec) return "";
        return formatCell(spec.value, spec.format, r.kind, displayCurrency, currencyDecimals);
      })
    )
  );

  autoTable(doc, {
    head,
    body,
    theme: "plain",
    startY: CONTENT_TOP,
    margin: { top: CONTENT_TOP, left: SIDE, right: SIDE, bottom: 34 },
    tableWidth: pageW - SIDE * 2,
    styles: {
      font: FONT,
      fontSize: 8,
      cellPadding: { top: 4, bottom: 4, left: 7, right: 7 },
      textColor: C.slate600,
      lineColor: C.slate100,
      lineWidth: { bottom: 0.5 },
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: C.slate50,
      textColor: C.slate500,
      fontStyle: "bold",
      fontSize: 7,
      halign: "left",
      lineColor: C.slate200,
      lineWidth: { top: 0.75, bottom: 1 },
    },
    columnStyles: {
      0: { halign: "left", cellWidth: Math.min(240, (pageW - SIDE * 2) * 0.32) },
    },
    didParseCell: (data: CellHookData) => {
      if (data.column.index > 0) data.cell.styles.halign = "right";
      if (data.section !== "body") return;

      const r = rows[data.row.index];
      if (!r) return;
      const isLabel = data.column.index === 0;
      const s = data.cell.styles;

      if (isLabel && r.indent) {
        s.cellPadding = { top: 4, bottom: 4, left: 7 + r.indent * 12, right: 7 };
      }

      switch (r.kind) {
        case "grandTotal":
          s.fillColor = C.slate900;
          s.textColor = C.white;
          s.fontStyle = "bold";
          s.lineColor = C.slate700;
          s.lineWidth = { bottom: 1 };
          break;
        case "header":
          s.fillColor = C.slate50;
          s.textColor = C.slate900;
          s.fontStyle = "bold";
          break;
        case "total":
          s.fillColor = C.slate50;
          s.textColor = C.slate900;
          s.fontStyle = "bold";
          break;
        case "highlight":
          s.fillColor = C.emerald50;
          s.textColor = C.emerald800;
          s.fontStyle = "bold";
          break;
        case "percent":
          s.fillColor = C.emerald50;
          s.textColor = C.emerald700;
          s.fontStyle = "italic";
          break;
        case "subtotal":
          s.textColor = C.slate900;
          s.fontStyle = "bold";
          s.lineWidth = { top: 0.5, bottom: 0.5 };
          s.lineColor = C.slate200;
          break;
        case "check": {
          s.fillColor = C.amber50;
          const spec = r.cells?.[data.column.index - 1];
          const mismatch = !isLabel && spec && Math.abs(spec.value) > 1;
          s.textColor = mismatch ? C.red600 : C.amber900;
          if (mismatch) s.fontStyle = "bold";
          break;
        }
        default: {
          if (!isLabel) {
            const spec = r.cells?.[data.column.index - 1];
            const v = spec?.value ?? 0;
            s.textColor = v > 0 ? C.emerald600 : v < 0 ? C.rose500 : C.slate300;
          }
        }
      }
    },
    didDrawPage: () => {
      // Dark title band (redrawn on every page, above the repeated header).
      doc.setFillColor(C.slate900[0], C.slate900[1], C.slate900[2]);
      doc.rect(SIDE, BAND_TOP, pageW - SIDE * 2, BAND_H, "F");
      doc.setTextColor(C.white[0], C.white[1], C.white[2]);
      doc.setFont(FONT, "bold");
      doc.setFontSize(14);
      doc.text(title, SIDE + 12, BAND_TOP + BAND_H / 2 + 1, { baseline: "middle" });
      doc.setFont(FONT, "normal");
      doc.setFontSize(9);
      doc.setTextColor(C.slate300[0], C.slate300[1], C.slate300[2]);
      doc.text(`Currency: ${displayCurrency}`, pageW - SIDE - 12, BAND_TOP + BAND_H / 2 + 1, {
        align: "right",
        baseline: "middle",
      });

      // Footer: absolute page number.
      const page = doc.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(C.slate500[0], C.slate500[1], C.slate500[2]);
      doc.text(`Page ${page}`, pageW / 2, pageH - 16, { align: "center" });
    },
  });
}

export async function exportStyledPdf(opts: {
  title: string;
  periodLabels: { key: string; label: string }[];
  rows: ExportRow[];
  displayCurrency: DisplayCurrency;
  fileName: string;
  currencyDecimals?: number;
}): Promise<void> {
  const { JsPDF, autoTable } = await loadPdfLibs();
  const doc = new JsPDF({
    orientation: "landscape",
    unit: "pt",
    format: widePage(opts.periodLabels.length + 1),
  });
  drawSection(doc, autoTable, opts);
  doc.save(opts.fileName);
}

/** Stitch several report sections into ONE document (each on a new page). */
export async function exportStyledPdfMulti(
  sections: PdfSection[],
  fileName: string
): Promise<void> {
  const { JsPDF, autoTable } = await loadPdfLibs();
  const maxCols = Math.max(...sections.map((s) => s.periodLabels.length + 1), 2);
  const doc = new JsPDF({ orientation: "landscape", unit: "pt", format: widePage(maxCols) });
  sections.forEach((section, i) => {
    if (i > 0) doc.addPage();
    drawSection(doc, autoTable, section);
  });
  doc.save(fileName);
}
